import { Loading02 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { FLOATING_PANEL_ANIM_FROM_TOP } from '@/lib/base/overlays/panel-animation';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import {
  type BulkActionJob,
  type BulkActionJobResultSummary,
  type BulkActionJobStatus,
  TERMINAL_BULK_ACTION_JOB_STATUSES,
} from '@/types/crm';

import {
  getEntityTypeLabel,
  pluralizeEntityLabel,
} from '../../utils/entityTypeUtils';
import { useBulkActionJob } from '../hooks/useBulkActionJob';

const BANNER_CLASS = `fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 sm:gap-3 rounded-xl border border-border-secondary bg-bg-primary px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg ${FLOATING_PANEL_ANIM_FROM_TOP}`;

// Vertical offset (in rem) from the top of the viewport for the first banner —
// matches the former `top-6`. Stacked banners step down by
// BANNER_STACK_SPACING_REM so concurrent jobs each get a distinct, individually
// actionable slot instead of piling up at identical fixed coordinates.
const BANNER_BASE_TOP_REM = 1.5;
const BANNER_STACK_SPACING_REM = 3.5;

function bannerCopy(
  actionType: BulkActionJob['action_type'] | undefined,
  processed: number,
  total: number,
  label: string
) {
  switch (actionType) {
    case 'change_stage':
      return `Changing stage: ${processed}/${total} ${label}…`;
    case 'enrich':
      return `Enriching ${processed}/${total} ${label}…`;
    case 'delete':
      return `Deleting ${processed}/${total} ${label}…`;
    default:
      return `Processing ${processed}/${total} ${label}…`;
  }
}

function cancelDialogTitle(
  actionType: BulkActionJob['action_type'] | undefined
) {
  switch (actionType) {
    case 'change_stage':
      return 'Cancel stage change?';
    case 'enrich':
      return 'Cancel enrichment?';
    case 'delete':
      return 'Cancel deletion?';
    default:
      return 'Cancel action?';
  }
}

function cancelAriaLabel(actionType: BulkActionJob['action_type'] | undefined) {
  switch (actionType) {
    case 'change_stage':
      return 'Cancel stage change';
    case 'enrich':
      return 'Cancel enrichment';
    case 'delete':
      return 'Cancel deletion';
    default:
      return 'Cancel action';
  }
}

function actionVerb(
  actionType: BulkActionJob['action_type'] | undefined
): string {
  switch (actionType) {
    case 'change_stage':
      return 'updated';
    case 'enrich':
      return 'queued for enrichment';
    case 'delete':
      return 'deleted';
    default:
      return 'processed';
  }
}

function cancelConfirmLabel(
  actionType: BulkActionJob['action_type'] | undefined
): string {
  switch (actionType) {
    case 'change_stage':
      return 'Cancel stage change';
    case 'enrich':
      return 'Cancel enrichment';
    case 'delete':
      return 'Cancel deletion';
    default:
      return 'Cancel action';
  }
}

function cancelContinueLabel(
  actionType: BulkActionJob['action_type'] | undefined
): string {
  switch (actionType) {
    case 'change_stage':
      return 'Continue updating';
    case 'enrich':
      return 'Continue enriching';
    case 'delete':
      return 'Continue deleting';
    default:
      return 'Continue';
  }
}

function actionName(
  actionType: BulkActionJob['action_type'] | undefined
): string {
  switch (actionType) {
    case 'change_stage':
      return 'Stage change';
    case 'enrich':
      return 'Enrichment';
    case 'delete':
      return 'Deletion';
    default:
      return 'Action';
  }
}

function terminalToast(
  actionType: BulkActionJob['action_type'],
  summary: BulkActionJobResultSummary | null,
  status: BulkActionJobStatus,
  label: string,
  pluralLabel: string,
  processed: number
): string | null {
  let count: number;
  if (!summary) {
    count = processed;
  } else {
    switch (actionType) {
      case 'change_stage':
        count = summary.changed_count ?? processed;
        break;
      case 'enrich':
        count = summary.enriched_count ?? processed;
        break;
      default:
        count = summary.deleted_count ?? processed;
    }
  }
  const verb = actionVerb(actionType);
  if (status === 'completed') {
    return `${count} ${count === 1 ? label : pluralLabel} ${verb}.`;
  }
  if (status === 'completed_with_errors') {
    const ok = count;
    const failed = summary?.failed_count ?? 0;
    return `Job finished with errors: ${ok} succeeded, ${failed} failed.`;
  }
  if (status === 'failed') {
    return `${actionName(actionType)} failed. Please try again.`;
  }
  if (status === 'cancelled') {
    return `${actionName(actionType)} cancelled.`;
  }
  return null;
}

export interface BulkActionBannerProps {
  orgId: number;
  activeJobId: number;
  totalCount: number;
  // The entity_type_name of the active job (e.g. "ahoy_contact"). This is the
  // job's own type, not the current page's type — pass through from whatever
  // initiated the banner (sync dispatch uses the page entity type; revive uses
  // the persisted entity type). The banner derives both singular and plural
  // labels from this value so progress text and terminal toasts always
  // describe the correct entity, even after navigation or re-attach.
  entityTypeName: string;
  onDismiss: () => void;
  /**
   * Zero-based position in a vertical stack of concurrent banners. Offsets the
   * banner's fixed `top` so overlapping jobs each stay visible and actionable.
   * Defaults to 0 (single banner at the base position).
   */
  stackIndex?: number;
  /** When true, the banner does NOT fire its own terminal/not-found toast; the caller handles it via onTerminal/onNotFound. Default false. */
  suppressTerminalToast?: boolean;
  /** Called once when the job reaches a terminal status (job is loaded). Fires before onDismiss. */
  onTerminal?: (job: BulkActionJob) => void;
  /**
   * Called once when the job can no longer be found (404) — expired or gone
   * before any terminal status was observed. No job is available in this case,
   * so it is a separate callback from onTerminal. Fires before onDismiss.
   */
  onNotFound?: () => void;
}

export function BulkActionBanner({
  orgId,
  activeJobId,
  totalCount,
  entityTypeName,
  onDismiss,
  stackIndex = 0,
  suppressTerminalToast = false,
  onTerminal,
  onNotFound,
}: BulkActionBannerProps) {
  const { job, error } = useBulkActionJob(orgId, activeJobId);
  const terminalToastFiredRef = useRef<number | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const processed = useMemo(
    () => job?.progress?.processed ?? 0,
    [job?.progress?.processed]
  );
  const total = useMemo(
    () => job?.progress?.total ?? totalCount,
    [job?.progress?.total, totalCount]
  );
  // Prefer the job's entity_type_name once it loads (authoritative), falling
  // back to the prop (initial render and any brief gap before the first poll
  // returns). The prop is the job's own type — not the current page's — so
  // both paths render the same labels.
  const effectiveEntityTypeName = job?.entity_type_name ?? entityTypeName;
  const effectiveEntityLabel = useMemo(
    () =>
      effectiveEntityTypeName
        ? getEntityTypeLabel(effectiveEntityTypeName).toLowerCase()
        : 'record',
    [effectiveEntityTypeName]
  );
  const effectivePluralEntityLabel = useMemo(
    () => pluralizeEntityLabel(effectiveEntityLabel),
    [effectiveEntityLabel]
  );
  const isCancellable = useMemo(() => {
    if (!job) {
      return true;
    }
    return job.status === 'running' || job.status === 'pending';
  }, [job]);

  // Leave undefined until the job loads so we render neutral copy instead of
  // briefly showing the wrong action's wording (e.g. "Deleting…" for an enrich
  // job). All banner helpers fall through to a generic message in that case.
  const actionType = job?.action_type;

  useEffect(() => {
    if (terminalToastFiredRef.current === activeJobId) {
      return;
    }
    if (error === 'not_found') {
      terminalToastFiredRef.current = activeJobId;
      if (!suppressTerminalToast) {
        toast.error('The bulk action job is no longer available.');
      }
      onNotFound?.();
      onDismiss();
      return;
    }
    if (!job) {
      return;
    }
    if (!TERMINAL_BULK_ACTION_JOB_STATUSES.has(job.status)) {
      return;
    }
    terminalToastFiredRef.current = activeJobId;
    const summary = job.result_summary;
    const message = terminalToast(
      job.action_type,
      summary,
      job.status,
      effectiveEntityLabel,
      effectivePluralEntityLabel,
      processed
    );
    if (!suppressTerminalToast && message) {
      if (job.status === 'completed' || job.status === 'cancelled') {
        toast.success(message);
      } else {
        toast.error(message);
      }
    }
    onTerminal?.(job);
    onDismiss();
  }, [
    activeJobId,
    error,
    job,
    processed,
    effectiveEntityLabel,
    effectivePluralEntityLabel,
    onDismiss,
    suppressTerminalToast,
    onTerminal,
    onNotFound,
  ]);

  useEffect(() => {
    terminalToastFiredRef.current = null;
  }, [activeJobId]);

  const handleOpenCancelConfirm = useCallback(
    () => setShowCancelConfirm(true),
    []
  );
  const handleCloseCancelConfirm = useCallback(
    () => setShowCancelConfirm(false),
    []
  );
  const handleConfirmCancel = useCallback(async () => {
    setIsCancelling(true);
    try {
      await apiPost<{ success: true }>(
        `/api/orgs/${orgId}/crm-events/bulk-action/jobs/${activeJobId}/cancel/`,
        {}
      );
    } catch {
      logger.warn('Failed to cancel bulk action job', {
        component: 'crm-bulk-action',
        org_id: orgId,
        bulk_action_job_id: activeJobId,
      });
      toast.error("We couldn't request cancellation. Try again.");
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  }, [orgId, activeJobId]);

  const bannerTop = `${BANNER_BASE_TOP_REM + stackIndex * BANNER_STACK_SPACING_REM}rem`;
  const banner = (
    <div
      role='status'
      aria-live='polite'
      className={BANNER_CLASS}
      style={{ top: bannerTop }}
    >
      <Loading02 className='size-4 animate-spin text-fg-secondary' />
      <span className='text-sm font-medium text-fg-primary whitespace-nowrap'>
        {bannerCopy(actionType, processed, total, effectivePluralEntityLabel)}
      </span>
      {isCancellable ? (
        <Button
          color='tertiary-destructive'
          size='sm'
          onClick={handleOpenCancelConfirm}
          aria-label={cancelAriaLabel(actionType)}
        >
          <span>Cancel</span>
        </Button>
      ) : null}
    </div>
  );
  const portalNode =
    typeof document !== 'undefined'
      ? createPortal(banner, document.body)
      : banner;
  return (
    <>
      {portalNode}
      <ConfirmationDialog
        isOpen={showCancelConfirm}
        title={cancelDialogTitle(actionType)}
        description={`${processed} of ${total} ${effectivePluralEntityLabel} already ${actionVerb(actionType)}.`}
        confirmLabel={cancelConfirmLabel(actionType)}
        cancelLabel={cancelContinueLabel(actionType)}
        variant='destructive'
        isLoading={isCancelling}
        onConfirm={handleConfirmCancel}
        onCancel={handleCloseCancelConfirm}
      />
    </>
  );
}
